/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveCurriculo, generatePdf, fixFormatting } from '../api';
import TextWithAIButton from '../components/TextWithAIButton';
import RepeatableBlock from '../components/RepeatableBlock';

type Curriculo = {
  dadosPessoais: any;
  formacao: any[];
  experiencias: any[];
  objetivo: any;
  cursosComplementares: any[];
};

// Lista de estados brasileiros
const estadosBrasileiros = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const emptyCurriculo = (): Curriculo => ({
  dadosPessoais: { nome: '', idade: '', pcd: 'Não', pcdDescricao: '', nacionalidade: '', rua: '', bairro: '', cidade: '', estado: '', telefone: '', telefone2: '', estadoCivil: '', disponibilidade: '', email: '', foto: '' },
  formacao: Array.from({ length: 3 }).map(() => ({ formacao: '', curso: '', instituicao: '', anoFinalizacao: '' })),
  experiencias: Array.from({ length: 5 }).map(() => ({ empresa: '', cargo: '', dataInicio: '', dataFim: '', atividades: '', empregoAtual: false })),
  objetivo: { setor: '', modalidade: '', observacoes: '' },
  cursosComplementares: Array.from({ length: 3 }).map(() => ({ curso: '', instituicao: '', ano: '', duracao: '' })),
});

// Opções de formação acadêmica
const opcoesFormacao = [
  'Ensino Fundamental Incompleto',
  'Ensino Fundamental Completo',
  'Ensino Médio Incompleto',
  'Ensino Médio Completo',
  'Ensino Técnico Incompleto',
  'Ensino Técnico Completo',
  'Ensino Superior Incompleto',
  'Ensino Superior Completo',
  'Pós-Graduação Incompleta',
  'Pós-Graduação Completa',
  'Mestrado Incompleto',
  'Mestrado Completo',
  'Doutorado Incompleto',
  'Doutorado Completo'
];

// Formações que requerem especificar o curso
const formacoesComCurso = [
  'Ensino Técnico Incompleto',
  'Ensino Técnico Completo',
  'Ensino Superior Incompleto',
  'Ensino Superior Completo',
  'Pós-Graduação Incompleta',
  'Pós-Graduação Completa',
  'Mestrado Incompleto',
  'Mestrado Completo',
  'Doutorado Incompleto',
  'Doutorado Completo'
];

export default function CurriculoForm() {
  const navigate = useNavigate();
  const cpf = sessionStorage.getItem('cpf') || '';
  const anon = sessionStorage.getItem('anon') === '1';

  // Safe parse: evita que "null" (string) ou JSON inválido provoque form === null
  const savedRaw = sessionStorage.getItem('curriculoData');
  let parsedSaved: Curriculo | null = null;
  if (savedRaw) {
    try {
      parsedSaved = JSON.parse(savedRaw);
    } catch {
      parsedSaved = null;
    }
  }

  const [form, setForm] = useState<Curriculo>(parsedSaved && parsedSaved.dadosPessoais ? parsedSaved : emptyCurriculo());
  const [saving, setSaving] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const [showExp, setShowExp] = useState<boolean[]>([true, false, false, false, false]);
  const [showCurso, setShowCurso] = useState<boolean[]>([true, false, false]);
  // estado de visibilidade para formações (1 visível por padrão)
  const [showFormacao, setShowFormacao] = useState<boolean[]>([true, false, false]);

  // Handler para upload de foto
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione uma imagem válida (JPG, PNG, etc.)');
        return;
      }
      // Validar tamanho (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPath('dadosPessoais.foto', reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handler para remover foto
  const handleRemovePhoto = () => {
    setPath('dadosPessoais.foto', '');
  };

  useEffect(() => {
    if (!cpf && !anon) navigate('/cpf');
    // set visibility based on loaded data
    const newShowExp = [...showExp];
    form.experiencias.forEach((e, i) => { if (e.empresa && i < 4) newShowExp[i + 1] = true; });
    setShowExp(newShowExp);
    const newShowCurso = [...showCurso];
    form.cursosComplementares.forEach((c, i) => { if (c.curso && i < 2) newShowCurso[i + 1] = true; });
    setShowCurso(newShowCurso);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPath = (path: string, value: any) => {
    setForm(prev => {
      const copy: any = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = copy;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  // handlers para formação
  const handleFormationChange = (idx: number, field: string, value: any) => {
    const next = { ...form };
    (next.formacao[idx] as any)[field] = value;
    setForm(next);
  };
  const addFormation = (idx: number) => {
    const s = [...showFormacao];
    if (idx + 1 < s.length) s[idx + 1] = true;
    setShowFormacao(s);
  };

  const handleExpChange = (idx: number, field: string, value: any) => {
    const next = { ...form };
    (next.experiencias[idx] as any)[field] = value;
    setForm(next);
  };

  const addExperience = (idx: number) => {
    const s = [...showExp]; if (idx + 1 < s.length) s[idx + 1] = true; setShowExp(s);
  };

  const handleCourseChange = (idx: number, field: string, value: any) => {
    const next = { ...form }; (next.cursosComplementares[idx] as any)[field] = value; setForm(next);
  };

  const addCourse = (idx: number) => { const s = [...showCurso]; if (idx + 1 < s.length) s[idx + 1] = true; setShowCurso(s); };

  const validate = () => {
    const p = form.dadosPessoais;
    if (!p.nome.trim()) return 'Nome obrigatório';
    if (!p.email.trim() || !p.email.includes('@')) return 'E-mail inválido';
    if (!p.telefone.trim()) return 'Telefone obrigatório';
    return null;
  };

  const handleSaveAndGeneratePDF = async () => {
    const err = validate(); if (err) { alert(err); return; }
    setSaving(true);
    try {
      // 1. Corrige formatação e português automaticamente antes de salvar
      let correctedForm = form;
      try {
        const fixResult = await fixFormatting(form);
        if (fixResult.success && fixResult.data) {
          correctedForm = fixResult.data;
          setForm(correctedForm);
        }
      } catch (fixErr) {
        console.warn('[DEBUG] Correção automática falhou, continuando com dados originais:', fixErr);
      }

      // 2. Salva o currículo (tanto para usuários logados quanto anônimos)
      let currentCpf = sessionStorage.getItem('cpf');
      if (!currentCpf) {
        currentCpf = Date.now().toString();
        sessionStorage.setItem('cpf', currentCpf);
      }
      console.log('[DEBUG] Salvando currículo com CPF:', currentCpf, 'anon:', anon);
      const saveResult = await saveCurriculo({ cpf: currentCpf, data: correctedForm });
      console.log('[DEBUG] Resultado do save:', saveResult);
      sessionStorage.setItem('curriculoData', JSON.stringify(correctedForm));
      if (!anon) {
        setSavedOnce(true);
      }
      
      // 3. Gera o PDF com os dados corrigidos
      if (!anon) {
        const blob = await generatePdf(cpf, correctedForm);
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const a = document.createElement('a'); a.href = url; a.download = `curriculo_${cpf}.pdf`; a.click();
      } else {
        const blob = await generatePdf('anonimo', correctedForm);
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const a = document.createElement('a'); a.href = url; a.download = `curriculo_anonimo.pdf`; a.click();
      }
    } catch (e: any) {
      console.error('[DEBUG] Erro ao salvar:', e?.response?.data || e?.message || e);
      alert('Erro ao salvar ou gerar PDF: ' + (e?.response?.data?.error || e?.message || 'Erro desconhecido'));
    } finally { setSaving(false); }
  };

  // Lista de nacionalidades (em português) - pode ser estendida
  const nacionalidades = [
    'Brasileira', 'Argentina', 'Boliviana', 'Chilena', 'Colombiana', 'Equatoriana',
    'Paraguaia', 'Peruana', 'Uruguaia', 'Venezuelana',
    'Portuguesa', 'Espanhola', 'Inglesa', 'Americana', 'Canadense',
    'Alemã', 'Italiana', 'Francesa', 'Japonesa', 'Chinesa', 'Indiana',
    'Australiana', 'Sul-Africana', 'Russa', 'Mexicana', 'Turca'
  ];

  // Opções para estado civil
  const estadosCivis = ['Selecione...', 'Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável'];

  // Opções de disponibilidade conforme solicitado
  const disponibilidades = [
    'manhã',
    'tarde',
    'noite',
    'manhã e tarde',
    'tarde e noite',
    'manhã e noite'
  ];

  // Opções de modalidade para o campo objetivo
  const modalidades = [
    'CLT',
    'Autônomo / Freelancer',
    'PJ',
    'Estágio',
    'Jovem Aprendiz'
  ];

  // Função para deletar formação
  const deleteFormation = (idx: number) => {
    const nextForm = { ...form };
    nextForm.formacao[idx] = { formacao: '', curso: '', instituicao: '', anoFinalizacao: '' };
    setForm(nextForm);
    const s = [...showFormacao];
    s[idx] = false;
    setShowFormacao(s);
  };

  // Função para deletar experiência
  const deleteExperience = (idx: number) => {
    const nextForm = { ...form };
    nextForm.experiencias[idx] = { empresa: '', cargo: '', dataInicio: '', dataFim: '', atividades: '', empregoAtual: false };
    setForm(nextForm);
    const s = [...showExp];
    s[idx] = false;
    setShowExp(s);
  };

  // Função para deletar curso
  const deleteCourse = (idx: number) => {
    const nextForm = { ...form };
    nextForm.cursosComplementares[idx] = { curso: '', instituicao: '', ano: '', duracao: '' };
    setForm(nextForm);
    const s = [...showCurso];
    s[idx] = false;
    setShowCurso(s);
  };

  const Tip = ({ text }: { text: string }) => (
    <span className="help-tip" data-tip={text}>?</span>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', paddingTop: 24 }}>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <img src="/assets/icones-curriculo-05.svg" alt="Ícone Acesso Profissional" style={{ width: '48px', height: '48px', marginBottom: '8px' }} />
        <h2 style={{ color: 'var(--primary-color)', fontWeight: 700, fontSize: 26, marginBottom: 8 }}>Editar Currículo</h2>
      </div>
      {!anon && <p style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 15, marginBottom: 18 }}>CPF: {cpf}</p>}

      <div className="section-title">Dados Pessoais</div>
      <div className="form-row" style={{ alignItems: 'flex-start' }}>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Nome * <Tip text="Nome completo como aparecerá no currículo" /></label>
          <input value={form.dadosPessoais.nome} onChange={(e) => setPath('dadosPessoais.nome', e.target.value)} placeholder="Ex: Maria Silva Santos" />
        </div>
        <div className="form-group" style={{ flex: 0, minWidth: 140, marginLeft: 16 }}>
          <label>Foto (opcional) <Tip text="Foto 3x4 opcional. Formatos: JPG, PNG. Máximo 5MB" /></label>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            {form.dadosPessoais.foto ? (
              <div style={{ position: 'relative' }}>
                <img 
                  src={form.dadosPessoais.foto} 
                  alt="Foto do currículo" 
                  style={{ 
                    width: 75, 
                    height: 100, 
                    objectFit: 'cover', 
                    borderRadius: 4, 
                    border: '1px solid #ccc' 
                  }} 
                />
                <button 
                  type="button" 
                  onClick={handleRemovePhoto}
                  style={{ 
                    position: 'absolute', 
                    top: -8, 
                    right: -8, 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    border: 'none', 
                    background: '#e74c3c', 
                    color: 'white', 
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Remover foto"
                >
                  ×
                </button>
              </div>
            ) : (
              <label 
                style={{ 
                  width: 75, 
                  height: 100, 
                  border: '2px dashed #ccc', 
                  borderRadius: 4, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  cursor: 'pointer',
                  fontSize: 11,
                  color: '#888',
                  textAlign: 'center',
                  padding: 4
                }}
              >
                <span style={{ fontSize: 24, marginBottom: 4 }}>📷</span>
                <span>Clique para adicionar</span>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handlePhotoUpload} 
                  style={{ display: 'none' }} 
                />
              </label>
            )}
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Idade <Tip text="Sua idade atual em anos" /></label>
          <input type="number" value={form.dadosPessoais.idade} onChange={(e) => setPath('dadosPessoais.idade', e.target.value)} placeholder="Ex: 28" />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>PCD <Tip text="Pessoa com Deficiência. Selecione 'Sim' se aplicável" /></label>
          <select value={form.dadosPessoais.pcd} onChange={(e) => setPath('dadosPessoais.pcd', e.target.value)}>
            <option>Não</option>
            <option>Sim</option>
          </select>
        </div>
        {form.dadosPessoais.pcd === 'Sim' && (
          <div className="form-group">
            <label>Descrição PCD <Tip text="Descreva brevemente o tipo de deficiência" /></label>
            <input value={form.dadosPessoais.pcdDescricao} onChange={(e) => setPath('dadosPessoais.pcdDescricao', e.target.value)} placeholder="Ex: Deficiência auditiva moderada" />
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Nacionalidade <Tip text="País de origem ou nacionalidade atual" /></label>
          <select value={form.dadosPessoais.nacionalidade} onChange={(e) => setPath('dadosPessoais.nacionalidade', e.target.value)}>
            <option value="">Selecione...</option>
            {nacionalidades.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Estado Civil <Tip text="Situação matrimonial atual" /></label>
          <select value={form.dadosPessoais.estadoCivil} onChange={(e) => setPath('dadosPessoais.estadoCivil', e.target.value)}>
            {estadosCivis.map((s) => (
              <option key={s} value={s === 'Selecione...' ? '' : s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group"><label>Rua <Tip text="Nome da rua e número do endereço" /></label><input value={form.dadosPessoais.rua} onChange={(e) => setPath('dadosPessoais.rua', e.target.value)} placeholder="Ex: Rua das Flores, 123" /></div>
        <div className="form-group"><label>Bairro <Tip text="Bairro onde você mora" /></label><input value={form.dadosPessoais.bairro} onChange={(e) => setPath('dadosPessoais.bairro', e.target.value)} placeholder="Ex: Centro" /></div>
      </div>

      <div className="form-row">
        <div className="form-group"><label>Cidade <Tip text="Cidade onde você reside atualmente" /></label><input value={form.dadosPessoais.cidade} onChange={(e) => setPath('dadosPessoais.cidade', e.target.value)} placeholder="Ex: São Paulo" /></div>
        <div className="form-group">
          <label>Estado <Tip text="Estado (UF) onde você mora" /></label>
          <select value={form.dadosPessoais.estado} onChange={(e) => setPath('dadosPessoais.estado', e.target.value)}>
            <option value="">Selecione...</option>
            {estadosBrasileiros.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Telefone * <Tip text="Número principal com DDD. Ex: (11) 99999-9999" /></label>
          <input value={form.dadosPessoais.telefone} onChange={(e) => setPath('dadosPessoais.telefone', e.target.value)} placeholder="Ex: (11) 99999-9999" />
        </div>
        <div className="form-group"><label>Segundo Telefone <Tip text="Telefone alternativo para contato" /></label><input value={form.dadosPessoais.telefone2} onChange={(e) => setPath('dadosPessoais.telefone2', e.target.value)} placeholder="Ex: (11) 98888-8888" /></div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Disponibilidade <Tip text="Turnos em que você pode trabalhar" /></label>
          <select value={form.dadosPessoais.disponibilidade} onChange={(e) => setPath('dadosPessoais.disponibilidade', e.target.value)}>
            <option value="">Selecione...</option>
            {disponibilidades.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      <div className="form-group"><label>E-mail * <Tip text="E-mail para o recrutador entrar em contato" /></label><input value={form.dadosPessoais.email} onChange={(e) => setPath('dadosPessoais.email', e.target.value)} placeholder="Ex: maria.silva@email.com" /></div>

      {/* Formação */}
      <RepeatableBlock
        title="Formação"
        items={form.formacao}
        show={showFormacao}
        onAdd={addFormation}
        showAddAt={(idx) =>
          showFormacao[idx] &&
          (idx === showFormacao.lastIndexOf(true)) &&
          idx < form.formacao.length - 1
        }
        renderItem={(f: any, idx: number) => {
          // Só permite excluir se houver mais de um bloco visível
          const canDelete = showFormacao.filter(Boolean).length > 1;
          const mostrarCamposCurso = formacoesComCurso.includes(f.formacao);
          return (
            <div>
              <div className="form-row">
                <div className="form-group">
                  <label>Formação <Tip text="Nível de escolaridade (ex: Ensino Médio, Superior)" /></label>
                  <select value={f.formacao} onChange={(e) => {
                    handleFormationChange(idx, 'formacao', e.target.value);
                    // Limpar o campo curso se a formação não requer
                    if (!formacoesComCurso.includes(e.target.value)) {
                      handleFormationChange(idx, 'curso', '');
                    }
                  }}>
                    <option value="">Selecione...</option>
                    {opcoesFormacao.map((op) => (
                      <option key={op} value={op}>{op}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group"><label>Instituição <Tip text="Escola, faculdade ou universidade onde estudou" /></label><input value={f.instituicao} onChange={(e) => handleFormationChange(idx, 'instituicao', e.target.value)} placeholder="Ex: Escola Estadual José Bonifácio" /></div>
              </div>
              {mostrarCamposCurso && (
                <div className="form-row">
                  <div className="form-group"><label>Curso <Tip text="Nome do curso realizado" /></label><input value={f.curso || ''} onChange={(e) => handleFormationChange(idx, 'curso', e.target.value)} placeholder="Ex: Administração de Empresas" /></div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group"><label>Ano de finalização <Tip text="Ano em que concluiu ou previsão de conclusão" /></label><input type="number" value={f.anoFinalizacao} onChange={(e) => handleFormationChange(idx, 'anoFinalizacao', e.target.value)} placeholder="Ex: 2020" /></div>
              </div>
              {canDelete && (
                <button className="btn btn-danger" style={{ marginTop: 8 }} onClick={() => deleteFormation(idx)}>Excluir</button>
              )}
            </div>
          );
        }}
      />

      {/* Experiências */}
      <RepeatableBlock
        title="Experiências Profissionais"
        items={form.experiencias}
        show={showExp}
        onAdd={addExperience}
        showAddAt={(idx) =>
          showExp[idx] &&
          (idx === showExp.lastIndexOf(true)) &&
          idx < form.experiencias.length - 1
        }
        renderItem={(e: any, idx: number) => {
          const canDelete = showExp.filter(Boolean).length > 1;
          const isEmpregoAtual = idx === 0 && e.empregoAtual;
          return (
            <div>
              <div className="form-group"><label>Empresa <Tip text="Nome da empresa onde trabalhou ou trabalha" /></label><input value={e.empresa} onChange={(ev) => handleExpChange(idx, 'empresa', ev.target.value)} placeholder="Ex: Supermercado Bom Preço" /></div>
              <div className="form-row">
                <div className="form-group"><label>Cargo <Tip text="Função ou posição que exerceu" /></label><input value={e.cargo} onChange={(ev) => handleExpChange(idx, 'cargo', ev.target.value)} placeholder="Ex: Atendente" /></div>
                <div className="form-group"><label>Período (início) <Tip text="Mês e ano em que começou" /></label><input type="month" value={e.dataInicio} onChange={(ev) => handleExpChange(idx, 'dataInicio', ev.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Período (fim) <Tip text="Mês e ano em que saiu. Deixe vazio se ainda trabalha lá" /></label>
                  <input 
                    type="month" 
                    value={isEmpregoAtual ? '' : e.dataFim} 
                    onChange={(ev) => handleExpChange(idx, 'dataFim', ev.target.value)} 
                    disabled={isEmpregoAtual}
                    style={isEmpregoAtual ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                  />
                </div>
                {idx === 0 && (
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: 0 }}>
                      <input 
                        type="checkbox" 
                        checked={e.empregoAtual || false} 
                        onChange={(ev) => {
                          handleExpChange(idx, 'empregoAtual', ev.target.checked);
                          if (ev.target.checked) {
                            handleExpChange(idx, 'dataFim', '');
                          }
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      Meu emprego atual
                    </label>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Atividades <Tip text="Descreva as principais tarefas e responsabilidades do cargo" /></label>
                <TextWithAIButton
                  value={e.atividades}
                  onChange={(v) => handleExpChange(idx, 'atividades', v)}
                  placeholder="Descreva suas atividades..."
                  fieldKey={`experiencias.${idx}.atividades`}
                  context={form}
                />
              </div>
              {canDelete && (
                <button className="btn btn-danger" style={{ marginTop: 8 }} onClick={() => deleteExperience(idx)}>Excluir</button>
              )}
            </div>
          );
        }}
      />

      {/* Cursos Complementares */}
      <RepeatableBlock
        title="Cursos Complementares"
        items={form.cursosComplementares}
        show={showCurso}
        onAdd={addCourse}
        showAddAt={(idx) =>
          showCurso[idx] &&
          (idx === showCurso.lastIndexOf(true)) &&
          idx < form.cursosComplementares.length - 1
        }
        renderItem={(c: any, idx: number) => {
          const canDelete = showCurso.filter(Boolean).length > 1;
          return (
            <div>
              <div className="form-row">
                <div className="form-group"><label>Curso <Tip text="Nome do curso complementar realizado" /></label><input value={c.curso} onChange={(ev) => handleCourseChange(idx, 'curso', ev.target.value)} placeholder="Ex: Informática Básica" /></div>
                <div className="form-group"><label>Instituição <Tip text="Onde realizou o curso (ex: SENAC, SENAI)" /></label><input value={c.instituicao} onChange={(ev) => handleCourseChange(idx, 'instituicao', ev.target.value)} placeholder="Ex: SENAC" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Ano <Tip text="Ano em que realizou o curso" /></label><input type="number" value={c.ano} onChange={(ev) => handleCourseChange(idx, 'ano', ev.target.value)} placeholder="Ex: 2022" /></div>
                <div className="form-group"><label>Duração <Tip text="Carga horária total do curso" /></label><input value={c.duracao} onChange={(ev) => handleCourseChange(idx, 'duracao', ev.target.value)} placeholder="Ex: 40 horas" /></div>
              </div>
              {canDelete && (
                <button className="btn btn-danger" style={{ marginTop: 8 }} onClick={() => deleteCourse(idx)}>Excluir</button>
              )}
            </div>
          );
        }}
      />

      <div className="section-title">Objetivo Profissional</div>
      <div className="form-row">
        <div className="form-group">
          <label>Modalidade <Tip text="Tipo de contrato desejado (CLT, estágio, PJ etc.)" /></label>
          <select value={form.objetivo.modalidade} onChange={(e) => setPath('objetivo.modalidade', e.target.value)}>
            <option value="">Selecione...</option>
            {modalidades.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label>Observações <Tip text="Descreva seu objetivo profissional e área de interesse" /></label>
        <TextWithAIButton
          value={form.objetivo.observacoes}
          onChange={(v) => setPath('objetivo.observacoes', v)}
          placeholder="Descreva seu objetivo..."
          fieldKey="objetivo.observacoes"
          context={form}
        />
      </div>

      <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={handleSaveAndGeneratePDF} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src="/assets/logos-07.svg" alt="" style={{ width: 40, height: 40, filter: 'brightness(0) invert(1)' }} />
          {saving ? '⏳ Corrigindo e Gerando PDF...' : '✨ Salvar e Gerar PDF'}
        </button>
        {savedOnce && <button className="btn btn-secondary" onClick={() => { /* permanece na edição */ }}>Editar novamente</button>}
        <button
          className="btn btn-secondary"
          onClick={() => {
            sessionStorage.removeItem('anon');
            if (anon) {
              navigate('/');
            } else {
              navigate('/cpf');
            }
          }}
          style={{ marginLeft: 'auto' }}
        >
          Voltar {anon ? 'ao início' : 'ao CPF'}
        </button>
      </div>
    </div>
  );
}
